/**
 * Metric Wisdom Registry — single source for Group A education copy.
 * Subtitle (L1) · Learn tooltip (L2) · learnUrl for full guides (L3).
 * Reuses investor dictionary explanations when a mapped term exists.
 */

import { getDictionaryTerm } from "@/data/investorDictionary";
import { getMetric, METRIC_KIND, METRIC_UNITS } from "./metricRegistry";

/** Dedicated /learn/metrics pages — add metricIds here when pages ship. */
export const METRIC_LEARN_PAGES = new Set();

const KIND_TO_VALUE_TYPE = {
  [METRIC_KIND.ACTUAL]: "actual",
  [METRIC_KIND.ESTIMATE]: "estimate",
  [METRIC_KIND.DERIVED]: "derived",
  [METRIC_KIND.USER_CUSTOM]: "user-custom-display-only",
};

const UNIT_TO_WISDOM_UNIT = {
  [METRIC_UNITS.CURRENCY]: "currency",
  [METRIC_UNITS.PERCENT]: "percentage",
  [METRIC_UNITS.RATIO]: "ratio",
  [METRIC_UNITS.SHARES]: "shares",
  [METRIC_UNITS.COUNT]: "score",
};

/** metricId → investor dictionary term id (when ids differ). */
export const METRIC_DICTIONARY_TERM_MAP = {
  revenue: "revenue",
  net_income: "earnings",
  eps_diluted: "eps",
  operating_cash_flow: "operating_cash_flow",
  capital_expenditure: "capex",
  free_cash_flow: "free_cash_flow",
  fcf_margin: "free_cash_flow_margin",
  gross_margin: "gross_margin",
  operating_margin: "operating_margin",
  net_margin: "net_margin",
  roic: "return_on_invested_capital",
  roe: "return_on_equity",
  roa: "return_on_assets",
  fcf_conversion: "cash_conversion",
  cash: "cash",
  debt: "debt",
  net_debt: "net_debt",
  trailing_pe: "pe_ratio",
  forward_pe: "forward_pe",
  peg: "peg_ratio",
  ev_to_sales: "price_to_sales",
  price_to_free_cash_flow: "price_to_free_cash_flow",
  revenue_growth: "revenue_growth",
  piotroski_score: "piotroski-f-score",
  altman_z_score: "altman-z-score",
};

/** Group-level fallback copy when a metric lacks curated tooltip fields (M7). */
const GROUP_WISDOM_FALLBACKS = {
  financials: {
    what: (label) => `Reported ${label} from the income statement.`,
    good: "Consistent trends over multiple periods with stable underlying drivers.",
    why: "Shows business scale and profitability from reported financials.",
    watch: "One-time items and accounting changes can shift a single period.",
  },
  "cash-flow": {
    what: (label) => `Reported ${label} from the cash flow statement.`,
    good: "Cash trends that align with operating performance over time.",
    why: "Cash flow is harder to dress up than accounting earnings.",
    watch: "Working-capital timing can make a single quarter misleading.",
  },
  margins: {
    what: (label) => `${label} — profit or cash as a share of revenue.`,
    good: "Stable or improving margins vs history and peers.",
    why: "Margins show pricing power and cost discipline.",
    watch: "Mix shifts can move margins without true unit-economics change.",
  },
  growth: {
    what: (label) => `Period-over-period change in ${label}.`,
    good: "Durable growth with sensible reinvestment and stable share count.",
    why: "Growth pace helps owners track momentum.",
    watch: "A single strong or weak year can distort the trend.",
  },
  valuation: {
    what: (label) => `Market ${label} — price relative to reported fundamentals.`,
    good: "Context-dependent — compare vs own history and sector norms.",
    why: "Valuation multiples frame what the market is paying today.",
    watch: "Multiples move with rates, sentiment, and estimate revisions.",
  },
  quality: {
    what: (label) => `${label} — efficiency and quality signal from reported data.`,
    good: "Sustained strong readings over many periods.",
    why: "Quality metrics hint at durability of competitive advantage.",
    watch: "Definitions vary — compare trends within one company.",
  },
  "balance-sheet": {
    what: (label) => `Reported ${label} from the balance sheet or leverage ratios.`,
    good: "Liquidity and leverage that match business risk profile.",
    why: "Balance sheet strength affects resilience and optionality.",
    watch: "Point-in-time snapshots can miss seasonal working-capital swings.",
  },
  "capital-allocation": {
    what: (label) => `${label} — how management deploys owner capital.`,
    good: "Capital returns and reinvestment aligned with durable value creation.",
    why: "Shows whether cash goes to owners, debt, M&A, or dilution.",
    watch: "A single year's buyback or dividend may not reflect policy.",
  },
  comparisons: {
    what: (label) => `${label} — raw descriptive comparison chart.`,
    good: "Use for trajectory context — not a verdict.",
    why: "Compares two dimensions without scoring or commands.",
    watch: "Descriptive only — no cheap/expensive or buy/sell language.",
  },
  "price-context": {
    what: (label) => `${label} from live quote and price history.`,
    good: "Context on where price sits vs recent history.",
    why: "Price-context charts describe market movement, not thesis.",
    watch: "Timing indicators — not conviction or scoring inputs.",
  },
};

const WISDOM_CURATED = {
  revenue: {
    subtitle: "Shows demand and scale — strongest when it converts into margin and cash.",
    good: "Consistent growth over time, ideally with stable or expanding margins.",
    formula: "income-statement.revenue",
    group: "financials",
    higherIsUsuallyBetter: "context-dependent",
    learnUrl: "/learn/metrics/revenue",
  },
  gross_profit: {
    subtitle: "Revenue minus direct costs — early read on pricing power and unit economics.",
    what: "Revenue minus direct cost of delivering the product or service.",
    good: "Stable or rising gross profit with healthy gross margin.",
    why: "Shows pricing power and unit economics before overhead.",
    watch: "Commodity businesses can show revenue growth with shrinking gross profit.",
    formula: "income-statement.revenue − costOfRevenue",
    group: "financials",
    higherIsUsuallyBetter: "context-dependent",
  },
  operating_income: {
    subtitle: "Core operating profit — how the business performs before financing and taxes.",
    what: "Profit from core operations after operating expenses.",
    good: "Positive and growing operating income over multiple periods.",
    why: "Closer to how the underlying business performs day to day.",
    watch: "Can be distorted by large one-time charges or gains.",
    formula: "income-statement.operatingIncome",
    group: "financials",
    higherIsUsuallyBetter: true,
  },
  net_income: {
    subtitle: "Bottom-line accounting profit — useful when cash conversion follows.",
    good: "Consistent net income that converts to cash over time.",
    formula: "income-statement.netIncome",
    group: "financials",
    higherIsUsuallyBetter: "context-dependent",
  },
  eps_diluted: {
    subtitle: "Profit per diluted share — the per-owner lens on reported earnings.",
    good: "Growing EPS supported by real business performance, not only buybacks.",
    formula: "income-statement.netIncome ÷ weightedAverageShsOutDil",
    group: "financials",
    higherIsUsuallyBetter: "context-dependent",
  },
  ebitda: {
    subtitle: "Operating earnings before D&A — common bridge for enterprise multiples.",
    what: "Earnings before interest, taxes, depreciation, and amortization.",
    good: "Useful for comparing operating cash generation across capital structures.",
    why: "Common bridge metric for enterprise-value multiples.",
    watch: "Ignores capex and true cash needs of the business.",
    formula: "income-statement.ebitda",
    group: "financials",
    higherIsUsuallyBetter: "context-dependent",
  },
  operating_cash_flow: {
    subtitle: "Cash from running the business — harder to dress up than accounting earnings.",
    good: "OCF that tracks or exceeds net income over time.",
    formula: "cashflow-statement.operatingCashFlow",
    group: "cash-flow",
    higherIsUsuallyBetter: true,
  },
  capital_expenditure: {
    subtitle: "Cash reinvested in assets — under-spending can flatter near-term FCF.",
    good: "Capex that supports durable growth without starving the business.",
    formula: "cashflow-statement.capitalExpenditure",
    group: "cash-flow",
    higherIsUsuallyBetter: false,
  },
  free_cash_flow: {
    subtitle: "Cash left after reinvestment — the cleanest owner metric.",
    good: "Positive and growing over time, preferably faster than share count.",
    formula: "cashflow-statement.operatingCashFlow − capitalExpenditure",
    group: "cash-flow",
    higherIsUsuallyBetter: true,
    learnUrl: "/learn/metrics/free-cash-flow",
  },
  fcf_margin: {
    subtitle: "Owner cash per sales dollar — quality of revenue conversion to cash.",
    good: "Higher sustained FCF margin vs peers in the same industry.",
    formula: "freeCashFlow ÷ revenue × 100",
    group: "cash-flow",
    higherIsUsuallyBetter: true,
  },
  fcf_per_share: {
    subtitle: "Free cash flow per diluted share — cash-earning power per owner slice.",
    good: "Rising FCF/share alongside durable competitive position.",
    formula: "freeCashFlow ÷ weightedAverageShsOutDil",
    group: "cash-flow",
    higherIsUsuallyBetter: true,
  },
  custom_fcf: {
    subtitle: "Your adjusted owner-cash view — display only, never scored.",
    what: "Your adjusted view of owner cash after optional capex/SBC tweaks.",
    good: "Useful for sensitivity — not a score input.",
    why: "Lets you stress-test owner cash under your assumptions.",
    watch: "Display only — never feeds portfolio or conviction scores.",
    formula: "operatingCashFlow − |capex| − stockBasedCompensation (user-adjustable)",
    group: "cash-flow",
    higherIsUsuallyBetter: "context-dependent",
    valueType: "user-custom-display-only",
  },
  gross_margin: {
    subtitle: "Pricing power before overhead — the first profitability checkpoint.",
    good: "Stable or rising gross margin vs peers.",
    formula: "grossProfit ÷ revenue × 100",
    group: "margins",
    higherIsUsuallyBetter: true,
  },
  operating_margin: {
    subtitle: "Core operating profitability after running the business.",
    good: "Positive and improving over multiple periods.",
    formula: "operatingIncome ÷ revenue × 100",
    group: "margins",
    higherIsUsuallyBetter: true,
  },
  net_margin: {
    subtitle: "Bottom-line profit per sales dollar — watch tax and interest swings.",
    good: "Consistent net margin with cash conversion.",
    formula: "netIncome ÷ revenue × 100",
    group: "margins",
    higherIsUsuallyBetter: "context-dependent",
  },
  roic: {
    subtitle: "Shows how efficiently the company turns capital into profit.",
    good: "Sustained ROIC above the cost of capital, especially over many years.",
    formula: "key-metrics.returnOnInvestedCapital",
    group: "quality",
    higherIsUsuallyBetter: true,
    learnUrl: "/learn/metrics/roic",
  },
  roce: {
    subtitle: "Return on capital employed — operating returns on deployed capital.",
    what: "Profit relative to capital employed in the business.",
    good: "Sustained ROCE above peers and cost of capital.",
    why: "Shows whether operating assets earn attractive returns.",
    watch: "Definitions vary; compare trends within one company.",
    formula: "key-metrics.returnOnCapitalEmployed",
    group: "quality",
    higherIsUsuallyBetter: true,
  },
  roe: {
    subtitle: "Return on book equity — can be flattered by leverage.",
    good: "Strong ROE sustained without excessive leverage.",
    formula: "key-metrics.returnOnEquity",
    group: "quality",
    higherIsUsuallyBetter: "context-dependent",
  },
  roa: {
    subtitle: "Profit per dollar of assets — useful across capital-intensity levels.",
    good: "Higher ROA vs capital-intensive peers.",
    formula: "key-metrics.returnOnAssets",
    group: "quality",
    higherIsUsuallyBetter: true,
  },
  fcf_conversion: {
    subtitle: "Whether reported earnings are turning into owner-usable cash.",
    good: "FCF tracking or exceeding net income over time.",
    formula: "freeCashFlow ÷ netIncome × 100",
    group: "quality",
    higherIsUsuallyBetter: true,
  },
  reinvestment_rate: {
    subtitle: "How much revenue is plowed back into capex and R&D.",
    what: "Capex plus R&D as a share of revenue.",
    good: "Reinvestment that supports durable growth.",
    why: "Shows how much revenue is plowed back into the business.",
    watch: "High reinvestment can be good or wasteful depending on returns.",
    formula: "(|capex| + R&D) ÷ revenue × 100",
    group: "capital-allocation",
    higherIsUsuallyBetter: "context-dependent",
  },
  rnd_pct_revenue: {
    subtitle: "Innovation spend intensity — context-heavy by industry.",
    what: "Research and development spend as a share of revenue.",
    good: "Context-dependent by industry.",
    why: "Innovation investment intensity.",
    watch: "R&D spend does not guarantee productive output.",
    formula: "income-statement.researchAndDevelopmentExpenses ÷ revenue × 100",
    group: "capital-allocation",
    higherIsUsuallyBetter: "context-dependent",
  },
  sga_pct_revenue: {
    subtitle: "Overhead burden per sales dollar — scale should help over time.",
    what: "Selling, general, and administrative costs as a share of revenue.",
    good: "Stable or falling SG&A with scale.",
    why: "Overhead burden on each revenue dollar.",
    watch: "Can understate go-to-market spend if costs are capitalized elsewhere.",
    formula: "income-statement.sellingGeneralAndAdministrativeExpenses ÷ revenue × 100",
    group: "capital-allocation",
    higherIsUsuallyBetter: false,
  },
  cash: {
    subtitle: "Liquidity on hand — optionality buffer for owners and creditors.",
    good: "Enough liquidity for operations and optionality.",
    formula: "balance-sheet.cashAndCashEquivalents",
    group: "balance-sheet",
    higherIsUsuallyBetter: "context-dependent",
  },
  debt: {
    subtitle: "Useful leverage when controlled, dangerous when cash flows weaken.",
    good: "Manageable vs cash flow and assets.",
    formula: "balance-sheet.totalDebt",
    group: "balance-sheet",
    higherIsUsuallyBetter: false,
  },
  net_debt: {
    subtitle: "Debt after cash — net obligation the business still carries.",
    good: "Lower net debt vs EBITDA in stable businesses.",
    formula: "balance-sheet.totalDebt − cash",
    group: "balance-sheet",
    higherIsUsuallyBetter: false,
  },
  net_debt_to_ebitda: {
    subtitle: "Leverage vs earnings power — EBITDA is not cash flow.",
    what: "Net debt relative to EBITDA.",
    good: "Context-dependent; compare to industry norms.",
    why: "Leverage vs earnings power.",
    watch: "EBITDA is not cash flow.",
    formula: "key-metrics.netDebtToEBITDA",
    group: "balance-sheet",
    higherIsUsuallyBetter: false,
  },
  interest_coverage: {
    subtitle: "Cushion to service debt from operating earnings.",
    what: "Ability to pay interest from operating earnings.",
    good: "Higher coverage = more cushion.",
    why: "Debt service safety margin.",
    watch: "EBIT-based coverage ignores capex needs.",
    formula: "key-metrics.interestCoverage",
    group: "balance-sheet",
    higherIsUsuallyBetter: true,
  },
  piotroski_score: {
    subtitle: "Nine-point financial strength checklist — descriptive, not a verdict.",
    good: "Higher scores indicate more positive signals (0–9).",
    formula: "financial-scores.piotroskiScore",
    group: "quality",
    higherIsUsuallyBetter: true,
  },
  altman_z_score: {
    subtitle: "Structural distress model — fit varies by industry and capital structure.",
    good: "Higher Z generally indicates lower distress risk in the classic model.",
    formula: "financial-scores.altmanZScore",
    group: "balance-sheet",
    higherIsUsuallyBetter: true,
  },
  revenue_growth: {
    subtitle: "Top-line pace — quality depends on margins, cash, and source of growth.",
    good: "Durable organic growth with stable or improving unit economics.",
    formula: "YoY change in income-statement.revenue",
    group: "growth",
    higherIsUsuallyBetter: "context-dependent",
  },
  eps_growth: {
    subtitle: "Per-share earnings pace — check buybacks and one-time items.",
    what: "Year-over-year change in diluted earnings per share.",
    good: "EPS growth backed by operating improvement, not only repurchases.",
    why: "Per-share lens on whether owner earnings power is expanding.",
    watch: "Buybacks and accounting items can lift EPS without business improvement.",
    formula: "YoY change in income-statement.epsDiluted",
    group: "growth",
    higherIsUsuallyBetter: "context-dependent",
  },
  fcf_growth: {
    subtitle: "Whether owner cash is compounding — lumpy by nature.",
    what: "Year-over-year change in free cash flow.",
    good: "Growing FCF with sensible reinvestment and stable share count.",
    why: "Shows whether cash available to owners is expanding.",
    watch: "Working-capital timing can make a single year misleading.",
    formula: "YoY change in cashflow-statement.freeCashFlow",
    group: "growth",
    higherIsUsuallyBetter: "context-dependent",
  },
  gross_profit_growth: {
    subtitle: "Whether unit economics are improving as the business scales.",
    what: "Year-over-year change in gross profit.",
    good: "Gross profit growing alongside or faster than revenue.",
    why: "Tests whether pricing power and direct costs are improving.",
    watch: "Mix shifts can move gross profit without true pricing power.",
    formula: "YoY change in income-statement.grossProfit",
    group: "growth",
    higherIsUsuallyBetter: "context-dependent",
  },
  operating_income_growth: {
    subtitle: "Core profit growth — closer to operating momentum than net income.",
    what: "Year-over-year change in operating income.",
    good: "Operating income growing with stable margins.",
    why: "Shows whether the core business is gaining operating leverage.",
    watch: "One-time operating items can distort a single period.",
    formula: "YoY change in income-statement.operatingIncome",
    group: "growth",
    higherIsUsuallyBetter: "context-dependent",
  },
  revenue_per_share: {
    subtitle: "Sales scale per owner slice — separates growth from dilution.",
    what: "Revenue divided by diluted shares outstanding.",
    good: "Rising revenue per share with durable demand.",
    why: "Per-share view of top-line scale for owners.",
    watch: "Share issuance can shrink this even when total revenue grows.",
    formula: "income-statement.revenue ÷ weightedAverageShsOutDil",
    group: "growth",
    higherIsUsuallyBetter: "context-dependent",
  },
  book_value_per_share: {
    subtitle: "Accounting net worth per share — not the same as intrinsic value.",
    what: "Shareholders' equity divided by shares outstanding.",
    good: "Steady growth in book value without excessive dilution.",
    why: "Book-value lens for balance-sheet progress per owner.",
    watch: "Buybacks and write-downs can move book value without business change.",
    formula: "balance-sheet.totalStockholdersEquity ÷ shares",
    group: "balance-sheet",
    higherIsUsuallyBetter: "context-dependent",
  },
  eps_revision_trend: {
    subtitle: "Whether analyst EPS estimates are rising or falling.",
    what: "Direction of consensus EPS estimate revisions over recent months.",
    good: "Upward revisions with stable underlying assumptions.",
    why: "Shows how expectations are shifting before earnings print.",
    watch: "Estimate changes are opinions — not confirmed results.",
    formula: "analyst-estimates.epsAvg revision trend",
    group: "growth",
    valueType: "estimate",
    higherIsUsuallyBetter: "context-dependent",
  },
  trailing_pe: {
    subtitle: "Shows what investors pay for each dollar of past earnings.",
    good: "Reasonable relative to growth, durability, margins, and interest rates.",
    formula: "metrics-ratios.priceToEarningsRatio",
    group: "valuation",
    higherIsUsuallyBetter: "context-dependent",
    learnUrl: "/learn/metrics/pe-ratio",
  },
  forward_pe: {
    subtitle: "Live price vs next consensus EPS — expectations move with the market.",
    good: "Context-dependent — compare vs own history and sector.",
    formula: "quote.price ÷ next analyst epsAvg",
    group: "valuation",
    valueType: "derived",
    higherIsUsuallyBetter: "context-dependent",
  },
  ev_to_sales: {
    subtitle: "Enterprise price per sales dollar — common for high-growth names.",
    good: "Context-dependent vs growth and margins.",
    formula: "key-metrics.evToSales",
    group: "valuation",
    higherIsUsuallyBetter: "context-dependent",
  },
  ev_to_ebitda: {
    subtitle: "Enterprise multiple on EBITDA — ignores capex and working capital.",
    what: "Enterprise value relative to EBITDA.",
    good: "Compare vs own history and capital-intensive peers.",
    why: "Common multiple for operating cash-earnings power.",
    watch: "EBITDA is not cash flow — ignores capex and working capital.",
    formula: "key-metrics.evToEBITDA",
    group: "valuation",
    higherIsUsuallyBetter: "context-dependent",
  },
  price_to_free_cash_flow: {
    subtitle: "Price vs owner cash — inverse lens on cash yield.",
    good: "Lower can mean cheaper cash yield — context matters.",
    formula: "metrics-ratios.priceToFreeCashFlowRatio",
    group: "valuation",
    higherIsUsuallyBetter: "context-dependent",
  },
  fcf_yield: {
    subtitle: "Cash return potential vs price — spikes can mean distress, not value.",
    what: "Free cash flow yield — cash return potential vs price.",
    good: "Higher yield can mean more cash per dollar of market cap.",
    why: "Inverse lens on P/FCF for owners.",
    watch: "Yield spikes can reflect distress, not value.",
    formula: "key-metrics.freeCashFlowYield",
    group: "valuation",
    higherIsUsuallyBetter: "context-dependent",
  },
  earnings_yield: {
    subtitle: "Earnings power vs price — accounting earnings are not cash.",
    what: "Earnings power relative to price (inverse of P/E).",
    good: "Compare vs bonds and own history — not a floor.",
    why: "Earnings-based yield lens for owners.",
    watch: "Accounting earnings ≠ cash.",
    formula: "key-metrics.earningsYield",
    group: "valuation",
    higherIsUsuallyBetter: "context-dependent",
  },
  peg: {
    subtitle: "P/E relative to expected growth — estimates change the picture.",
    good: "Context tool — not a verdict.",
    formula: "metrics-ratios.priceToEarningsGrowthRatio",
    group: "valuation",
    higherIsUsuallyBetter: "context-dependent",
  },
  shares_outstanding: {
    subtitle: "Diluted share count — rising shares dilute per-owner economics.",
    what: "Weighted average diluted shares outstanding for the period.",
    good: "Stable or falling share count alongside growing per-share metrics.",
    why: "Shows dilution vs buyback impact on owner slice.",
    watch: "Share count can fall from buybacks while SBC still dilutes employees.",
    formula: "income-statement.weightedAverageShsOutDil",
    group: "capital-allocation",
    higherIsUsuallyBetter: false,
  },
  stock_based_compensation: {
    subtitle: "Equity paid to employees — real economic cost even when non-cash.",
    what: "Non-cash stock compensation expense from the cash flow statement.",
    good: "SBC that stays reasonable vs revenue and cash generation.",
    why: "Shows how much equity is issued to fund talent.",
    watch: "High SBC can dilute owners even when revenue grows.",
    formula: "cashflow-statement.stockBasedCompensation",
    group: "capital-allocation",
    higherIsUsuallyBetter: false,
  },
  share_buybacks: {
    subtitle: "Cash returned via repurchases — reduces share count when sustained.",
    what: "Cash spent repurchasing common stock (typically negative outflow).",
    good: "Buybacks funded by excess cash, not debt at any price.",
    why: "Shows management returning cash by shrinking the share base.",
    watch: "Repurchases at peak valuations can destroy per-share value.",
    formula: "cashflow-statement.commonStockRepurchased",
    group: "capital-allocation",
    higherIsUsuallyBetter: "context-dependent",
  },
  dividends_paid: {
    subtitle: "Cash returned to shareholders — income today vs reinvestment tomorrow.",
    what: "Cash dividends paid to common shareholders.",
    good: "Dividends supported by durable free cash flow.",
    why: "Shows explicit cash return policy to owners.",
    watch: "Dividends funded by debt or asset sales are not sustainable.",
    formula: "cashflow-statement.commonDividendsPaid",
    group: "capital-allocation",
    higherIsUsuallyBetter: "context-dependent",
  },
  acquisitions_net: {
    subtitle: "Cash spent on M&A — growth bought vs grown organically.",
    what: "Net cash used for acquisitions in the period.",
    good: "Acquisitions that earn returns above the cost of capital.",
    why: "Shows external growth and capital deployment choices.",
    watch: "Large deals can take years to prove value — or never do.",
    formula: "cashflow-statement.acquisitionsNet",
    group: "capital-allocation",
    higherIsUsuallyBetter: "context-dependent",
  },
  growth_vs_pe: {
    subtitle: "EPS growth plotted against trailing P/E — raw period-by-period view.",
    what: "Each point shows EPS growth (Y) and trailing P/E (X) for one period.",
    good: "Descriptive scatter — compare trajectory over time.",
    why: "Shows how growth and multiple moved together historically.",
    watch: "Raw descriptive only — no cheap/expensive verdict.",
    formula: "financial-growth.epsDilutedGrowth vs metrics-ratios.priceToEarningsRatio",
    group: "comparisons",
    higherIsUsuallyBetter: "context-dependent",
    valueType: "derived",
  },
  price_vs_fcf_growth: {
    subtitle: "Indexed price vs indexed FCF — both reset to 100 at range start.",
    what: "Dual-line chart comparing price trajectory to FCF trajectory.",
    good: "Descriptive indexed comparison over the selected range.",
    why: "Shows whether price and owner cash moved together.",
    watch: "Raw descriptive only — no interpretation language.",
    formula: "price history vs cashflow-statement.freeCashFlow (indexed)",
    group: "comparisons",
    higherIsUsuallyBetter: "context-dependent",
    valueType: "derived",
  },
  price: {
    subtitle: "Live and historical share price — market quote context only.",
    what: "Closing share price from market data.",
    good: "Use with fundamentals — price alone is not a thesis.",
    why: "Anchors valuation and price-context charts.",
    watch: "Not a scoring or decision-state input.",
    formula: "quote.price / historical close",
    group: "price-context",
    higherIsUsuallyBetter: "context-dependent",
  },
  dma200: {
    subtitle: "200-day moving average — medium-term price trend context.",
    what: "Average closing price over the prior 200 trading days.",
    good: "Trend context vs current price.",
    why: "Shows whether price is above or below a common trend line.",
    watch: "Lagging indicator — not predictive on its own.",
    formula: "200-day average of historical close",
    group: "price-context",
    higherIsUsuallyBetter: "context-dependent",
  },
  drawdown_pct: {
    subtitle: "Peak-to-trough decline — how far price has fallen from recent high.",
    what: "Percentage decline from the trailing peak in the selected range.",
    good: "Context on volatility and recent pain.",
    why: "Helps owners see drawdown depth without timing commands.",
    watch: "Descriptive only — not a buy/sell signal.",
    formula: "Derived from historical close prices",
    group: "price-context",
    valueType: "derived",
    higherIsUsuallyBetter: false,
  },
  rsi_14: {
    subtitle: "14-period RSI — momentum context, not a thesis input.",
    what: "Relative Strength Index over 14 periods from price changes.",
    good: "Extreme readings flag stretched near-term moves.",
    why: "Common timing-context indicator for price charts.",
    watch: "Not conviction scoring — descriptive sweetener only.",
    formula: "Derived from 14-day price gains/losses",
    group: "price-context",
    valueType: "derived",
    higherIsUsuallyBetter: "context-dependent",
  },
  distance_from_ath: {
    subtitle: "How far current price sits below the all-time high.",
    what: "Percentage below the trailing all-time high in range.",
    good: "Context on how much rebound room exists vs peak.",
    why: "Frames whether price is near highs or in a reset zone.",
    watch: "ATH can be years old — context matters.",
    formula: "quote.price vs trailing high",
    group: "price-context",
    valueType: "derived",
    higherIsUsuallyBetter: "context-dependent",
  },
};

function getDictionaryWisdomFields(dictionaryTermId, language = "en") {
  const term = getDictionaryTerm(dictionaryTermId);
  if (!term) return null;
  const lang = language === "th" ? "th" : "en";
  const exp = term.explanations?.[lang] || term.explanations?.en || {};
  return {
    label: term.term?.[lang] || term.term?.en,
    shortLabel: term.short_term?.[lang] || term.short_term?.en || undefined,
    what: exp.basic,
    why: exp.why_it_matters,
    watch: exp.common_mistake,
  };
}

function resolveSourceFields(metricId, metric, curated) {
  const endpoint = curated.sourceEndpoint
    || (metric?.source?.endpoint !== "derived" ? metric?.source?.endpoint : undefined);
  const field = curated.sourceField || metric?.source?.fields?.[0];
  const derivedFormula = curated.derivedFormula ?? metric?.derivedFormula ?? undefined;
  return { sourceEndpoint: endpoint, sourceField: field, derivedFormula };
}

function buildResolvedWisdom(metricId, language = "en") {
  const metric = getMetric(metricId);
  const curated = WISDOM_CURATED[metricId];
  if (!metric && !curated) return null;

  const dictionaryTermId = curated?.dictionaryTermId
    || METRIC_DICTIONARY_TERM_MAP[metricId]
    || metricId;
  const dictionary = getDictionaryWisdomFields(dictionaryTermId, language);

  const { sourceEndpoint, sourceField, derivedFormula } = resolveSourceFields(metricId, metric, curated || {});

  const valueType = curated?.valueType
    || (metric ? KIND_TO_VALUE_TYPE[metric.kind] : "actual");

  const unit = curated?.unit
    || (metric ? UNIT_TO_WISDOM_UNIT[metric.unit] || "raw" : "raw");

  const label = curated?.label || dictionary?.label || metric?.label || metricId;
  const shortLabel = curated?.shortLabel || dictionary?.shortLabel;
  const group = curated?.group || "financials";
  const groupFallback = GROUP_WISDOM_FALLBACKS[group] || GROUP_WISDOM_FALLBACKS.financials;

  const wisdom = {
    metricId,
    label,
    shortLabel,
    subtitle: curated?.subtitle || "",
    what: curated?.what || dictionary?.what || groupFallback.what(label),
    good: curated?.good || groupFallback.good,
    why: curated?.why || dictionary?.why || groupFallback.why,
    watch: curated?.watch || dictionary?.watch || groupFallback.watch,
    formula: curated?.formula || derivedFormula || sourceField || "",
    sourceEndpoint,
    sourceField,
    derivedFormula,
    valueType,
    unit,
    higherIsUsuallyBetter: curated?.higherIsUsuallyBetter,
    learnUrl: curated?.learnUrl,
    group,
    dictionaryTermId: dictionary ? dictionaryTermId : undefined,
  };

  return wisdom;
}

const wisdomCache = new Map();

export function getMetricWisdom(metricId, language = "en") {
  const cacheKey = `${metricId}:${language}`;
  if (wisdomCache.has(cacheKey)) return wisdomCache.get(cacheKey);
  const wisdom = buildResolvedWisdom(metricId, language);
  wisdomCache.set(cacheKey, wisdom);
  return wisdom;
}

export function hasMetricLearnPage(metricId) {
  const wisdom = getMetricWisdom(metricId);
  if (!wisdom?.learnUrl) return false;
  return METRIC_LEARN_PAGES.has(metricId);
}

export function getMetricSubtitle(metricId, language = "en") {
  return getMetricWisdom(metricId, language)?.subtitle || "";
}

const VALUE_TYPE_LABELS = {
  actual: "Actual",
  estimate: "Estimate",
  derived: "Derived",
  "user-custom-display-only": "User-custom",
};

export function buildMetricSourceLine(metricId, sourceMeta = {}) {
  const wisdom = getMetricWisdom(metricId);
  const metric = getMetric(metricId);
  if (!wisdom && !metric) return "Source metadata unavailable";
  if (!wisdom) return "Source metadata unavailable";

  if (wisdom.valueType === "user-custom-display-only" || metric?.kind === METRIC_KIND.USER_CUSTOM) {
    return "User-custom · display-only · not scored";
  }

  const valueType = sourceMeta.valueType || wisdom.valueType;
  const isDerived = valueType === "derived"
    || metric?.kind === METRIC_KIND.DERIVED
    || wisdom.sourceEndpoint === "derived"
    || metric?.source?.endpoint === "derived";

  if (isDerived && (wisdom.derivedFormula || metric?.derivedFormula)) {
    const formula = wisdom.derivedFormula || metric?.derivedFormula;
    const fields = (metric?.source?.fields || []).join(" + ");
    const sourcePart = fields ? ` · Source: ${fields}` : "";
    return `Derived: ${formula}${sourcePart}`;
  }

  const parts = [];
  const endpoint = wisdom.sourceEndpoint || metric?.source?.endpoint;
  const field = wisdom.sourceField || metric?.source?.fields?.[0];

  if (endpoint && field && endpoint !== "derived") {
    parts.push(`FMP ${endpoint}.${field}`);
  } else if (endpoint && endpoint !== "derived") {
    parts.push(`FMP ${endpoint}`);
  }

  if (valueType) parts.push(VALUE_TYPE_LABELS[valueType] || valueType);
  if (sourceMeta.period) parts.push(sourceMeta.period);
  if (sourceMeta.updated) parts.push(`upd ${sourceMeta.updated}`);

  return parts.length ? parts.join(" · ") : "Source metadata unavailable";
}

/** Backward-compatible tooltip shape for legacy callers. */
export function getMetricTooltip(metricId, language = "en") {
  const wisdom = getMetricWisdom(metricId, language);
  if (!wisdom) return null;
  return {
    title: wisdom.shortLabel ? `${wisdom.label} (${wisdom.shortLabel})` : wisdom.label,
    subtitle: wisdom.subtitle,
    what: wisdom.what,
    good: wisdom.good,
    why: wisdom.why,
    watch: wisdom.watch,
    formula: wisdom.formula,
    learnUrl: hasMetricLearnPage(metricId) ? wisdom.learnUrl : undefined,
    sourceLine: buildMetricSourceLine(metricId),
  };
}

export function getAllMetricWisdomIds() {
  return Object.keys(WISDOM_CURATED);
}
